// app/auth/page.tsx
'use client';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ResetPass } from '@/components/component/login-page/reset-pass';
import { Login } from '@/components/component/login-page/login';
import { Register } from '@/components/component/login-page/register';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams, useRouter } from 'next/navigation';
import { useAuth } from '@/lib/AuthContext';
import { RegistrationData } from '@/app/actions/userActions';
import { signIn, signOut } from 'next-auth/react';
import Image from 'next/image';
import { ChevronLeft, X, LogOut, User, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Simplify by using string literals directly
type FormMode = 'login' | 'register' | 'reset';

// Use this object for constants but not for type checking
const FormModes = {
  LOGIN: 'login' as FormMode,
  REGISTER: 'register' as FormMode,
  RESET_PASSWORD: 'reset' as FormMode,
};

// Create a beautiful modal component for already logged in users
const AlreadyLoggedInModal = React.memo(
  ({
    user,
    onClose,
    onLogout,
  }: {
    user: any;
    onClose: () => void;
    onLogout: () => Promise<void>;
  }) => {
    const router = useRouter();

    // Quick navigation handler
    const navigateTo = (path: string) => {
      router.push(path);
      onClose();
    };

    // Navigation links with more subtle styling
    const navLinks = [
      { name: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
      { name: 'Charts', path: '/charts', icon: 'LineChart' },
      { name: 'Forums', path: '/forums', icon: 'MessageSquare' },
      { name: 'Blog', path: '/blog', icon: 'FileText' },
      { name: 'Market', path: '/marketplace', icon: 'Store' },
    ];

    return (
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Backdrop with blur effect */}
        <motion.div
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        />

        {/* Modal content */}
        <motion.div
          className="relative z-10 w-full max-w-md overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-slate-900 to-slate-950 p-6 shadow-xl"
          initial={{ scale: 0.9, y: 20, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.9, y: 20, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        >
          {/* Decorative elements */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-50" />
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-40 -right-40 h-80 w-80 rounded-full bg-blue-500/10 blur-[100px]" />
            <div className="absolute -bottom-40 -left-40 h-80 w-80 rounded-full bg-purple-500/10 blur-[100px]" />
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute right-4 top-4 rounded-full p-1 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
          >
            <X size={18} />
          </button>

          {/* Quick Links Section - Refined Version */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-3 text-center">
              Quick Navigation
            </h4>
            <div className="grid grid-cols-5 gap-2">
              {navLinks.map((link) => (
                <button
                  key={link.path}
                  onClick={() => navigateTo(link.path)}
                  className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-all duration-300 text-xs flex flex-col items-center justify-center border border-white/10 group"
                >
                  <span className="group-hover:scale-105 transition-transform text-center">
                    {link.name}
                  </span>
                  <div className="mt-1 h-px w-0 group-hover:w-full bg-blue-500/40 transition-all duration-300" />
                </button>
              ))}
            </div>
            <div className="mt-4 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          </div>

          {/* Content */}
          <div className="flex flex-col items-center text-center">
            {/* User avatar with glow */}
            <div className="relative mb-5">
              <div className="absolute inset-0 rounded-full bg-blue-500/20 blur-md transform scale-110" />
              <div className="relative bg-gradient-to-b from-blue-400/30 to-purple-400/30 rounded-full p-0.5">
                {user.image ? (
                  <Image
                    src={user.image}
                    alt={user.name || 'User'}
                    width={64}
                    height={64}
                    className="rounded-full h-16 w-16 object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center h-16 w-16 rounded-full bg-slate-800 text-blue-400">
                    <User size={32} />
                  </div>
                )}
              </div>
            </div>

            <h3 className="text-xl font-bold text-white mb-1">
              Already Logged In
            </h3>

            <p className="text-white/70 mb-6">
              You&apos;re currently logged in as{' '}
              <span className="text-blue-400 font-medium">
                {user.name || user.email}
              </span>
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
              <Button
                onClick={onClose}
                className="flex-1 bg-white/5 hover:bg-white/10 text-white border border-white/10 backdrop-blur-sm flex items-center justify-center gap-2 group"
              >
                Continue Session
                <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>

              <Button
                onClick={onLogout}
                className="flex-1 bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 flex items-center justify-center gap-2 group"
              >
                <LogOut className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                Logout
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  }
);
AlreadyLoggedInModal.displayName = 'AlreadyLoggedInModal';

// Extract background animation to a separate component to prevent re-renders
const AnimatedBackground = React.memo(() => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Throttle mouse move updates to reduce state changes
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let lastUpdate = 0;
    const THROTTLE_MS = 50; // Only update every 50ms

    const handleMouseMove = (e: MouseEvent) => {
      const now = Date.now();
      if (now - lastUpdate > THROTTLE_MS) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          setMousePosition({ x: e.clientX, y: e.clientY });
        }, 5);
        lastUpdate = now;
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      clearTimeout(timeoutId);
    };
  }, []);

  return (
    <div className="fixed inset-0 -z-10">
      {/* Dynamic background gradient */}
      <div
        className="absolute inset-0 bg-gradient-radial from-blue-950 via-black to-black opacity-90"
        style={{
          backgroundPosition: `${mousePosition.x / 30}px ${mousePosition.y / 30}px`,
        }}
      />

      {/* Grid pattern */}
      <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-20" />

      {/* Animated orbs/blurs */}
      <div
        className="absolute w-[400px] h-[400px] rounded-full bg-blue-500/10 blur-[100px] animate-pulse-slow"
        style={{
          top: '10%',
          right: '5%',
          animationDelay: '0s',
          transform: `translate(${mousePosition.x / 50}px, ${mousePosition.y / 50}px)`,
        }}
      />
      <div
        className="absolute w-[300px] h-[300px] rounded-full bg-purple-500/10 blur-[100px] animate-pulse-slow"
        style={{
          bottom: '5%',
          left: '10%',
          animationDelay: '0.5s',
          transform: `translate(${-mousePosition.x / 70}px, ${-mousePosition.y / 70}px)`,
        }}
      />
      <div
        className="absolute w-[250px] h-[250px] rounded-full bg-cyan-500/5 blur-[80px] animate-pulse-slow"
        style={{
          top: '30%',
          left: '15%',
          animationDelay: '1s',
          transform: `translate(${-mousePosition.x / 60}px, ${mousePosition.y / 60}px)`,
        }}
      />

      {/* Subtle stars/particles */}
      <div className="absolute inset-0">
        <Image
          src="/stars-bg.svg"
          alt="Stars background"
          fill
          className="opacity-30"
          priority={false}
        />
      </div>
    </div>
  );
});
AnimatedBackground.displayName = 'AnimatedBackground';

// Enhanced back button as a memoized component
const BackButton = React.memo(({ onClick }: { onClick: () => void }) => (
  <div className="relative z-10 p-4 sm:p-6">
    <Button
      onClick={onClick}
      variant="ghost"
      className="group relative px-4 py-2 overflow-hidden bg-transparent text-white hover:bg-transparent transition-all duration-300"
    >
      {/* Button background glow - not applying to the icon */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-blue-600/10 rounded-full blur-md"></div>

      {/* Button content with icon animation - removed the background from the icon */}
      <div className="flex items-center relative z-10">
        {/* Icon without glow background */}
        <ChevronLeft className="h-5 w-5 mr-2 group-hover:-translate-x-1 transition-all duration-300 ease-out" />

        <span className="hidden sm:block text-sm font-medium text-white/80 group-hover:text-white transition-colors">
          Back to Home
        </span>
      </div>

      {/* Bottom accent line with animation */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 h-[1px] w-0 group-hover:w-full bg-gradient-to-r from-transparent via-blue-500 to-transparent transition-all duration-500"></div>
    </Button>
  </div>
));
BackButton.displayName = 'BackButton';

// Main Auth component
function Auth() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { user, login, logout } = useAuth();

  // State for showing the "already logged in" modal
  const [showAlreadyLoggedInModal, setShowAlreadyLoggedInModal] =
    useState(false);
  // Add this flag to track if login just happened
  const [justLoggedIn, setJustLoggedIn] = useState(false);

  const [formMode, setFormMode] = useState<FormMode>(() => {
    return (searchParams?.get('mode') as FormMode) || FormModes.LOGIN;
  });

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Check if user is already logged in when the component mounts
  useEffect(() => {
    // Only show the modal if the user exists AND they didn't just log in
    if (user && !justLoggedIn) {
      setShowAlreadyLoggedInModal(true);
    }
  }, [user, justLoggedIn]);

  // Memoize event handlers to prevent recreating on each render
  const handleBackNavigation = useCallback(() => {
    router.push('/');
  }, [router]);

  const handleLogin = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setIsLoading(true);
      setError('');

      try {
        const response = await fetch('/api/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        });

        const data = await response.json();

        if (data.success || data.status === 'ok') {
          // Set flag that we're in the process of logging in
          setJustLoggedIn(true);

          if (data.user && typeof data.user === 'string') {
            try {
              const payload = JSON.parse(atob(data.user.split('.')[1]));
              const userData = {
                name: payload.name,
                email: payload.email,
                id: payload.sub || payload.id || payload.email,
              };
              localStorage.setItem('token', data.user);
              login(userData);
            } catch (error) {
              console.error('JWT parsing error:', error);
              setError('Login failed: Invalid token format');
              // Reset the flag if login fails
              setJustLoggedIn(false);
            }
          } else {
            login(data.user);
          }
        } else {
          setError(data.message || 'Login failed');
        }
      } catch (error) {
        console.error('Login error:', error);
        setError('Failed to login. Please try again.');
        // Reset the flag if login fails
        setJustLoggedIn(false);
      } finally {
        setIsLoading(false);
      }
    },
    [email, password, login]
  );

  const handleRegistration = useCallback(
    async (data: RegistrationData) => {
      setIsLoading(true);
      setError('');

      try {
        // Set flag that we're in the process of registering
        setJustLoggedIn(true);

        const { registerUser } = await import('@/app/actions/userActions');
        const result = await registerUser(data);

        if (result.success) {
          login(result.user);
        } else {
          setError(result.message || 'Registration failed');
          // Reset the flag if registration fails
          setJustLoggedIn(false);
        }
      } catch (error) {
        console.error('Registration error:', error);
        setError('Failed to register. Please try again.');
        // Reset the flag if registration fails
        setJustLoggedIn(false);
      } finally {
        setIsLoading(false);
      }
    },
    [login]
  );

  const handleGoogleSignIn = useCallback(async () => {
    setIsLoading(true);
    // Set flag that we're in the process of logging in with Google
    setJustLoggedIn(true);

    try {
      await signIn('google', { callbackUrl: '/dashboard' });
    } catch (error) {
      console.error('Google login error:', error);
      setError('Failed to login with Google. Please try again.');
      // Reset the flag if Google login fails
      setJustLoggedIn(false);
      setIsLoading(false);
    }
  }, []);

  // Handle logout from the modal
  const handleLogout = useCallback(async () => {
    try {
      // Clear stored user data
      localStorage.removeItem('user');
      localStorage.removeItem('token');

      // Call the logout function from auth context
      await logout();

      // Close the modal and refresh the page
      setShowAlreadyLoggedInModal(false);

      // Optional: redirect to home after logout
      // router.push('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  }, [logout]);

  const closeModal = useCallback(() => {
    setShowAlreadyLoggedInModal(false);
  }, []);

  const toggleToLogin = useCallback(() => {
    setError('');
    setFormMode(FormModes.LOGIN);
  }, []);

  const toggleToRegister = useCallback(() => {
    setError('');
    setFormMode(FormModes.REGISTER);
  }, []);

  const toggleToResetPassword = useCallback(() => {
    setError('');
    setFormMode(FormModes.RESET_PASSWORD);
  }, []);

  // Memoize form components to prevent unnecessary re-renders
  const currentForm = useMemo(() => {
    switch (formMode) {
      case FormModes.LOGIN:
        return (
          <Login
            onEmailChange={setEmail}
            onPasswordChange={setPassword}
            onSubmit={handleLogin}
            onSignUpClick={toggleToRegister}
            onForgotPasswordClick={toggleToResetPassword}
            onGoogleSignIn={handleGoogleSignIn}
            email={email}
            password={password}
            error={error}
          />
        );
      case FormModes.REGISTER:
        return (
          <Register
            onSubmit={handleRegistration}
            onBackToLogin={toggleToLogin}
            onGoogleSignIn={handleGoogleSignIn}
            error={error}
          />
        );
      case FormModes.RESET_PASSWORD:
        return (
          <ResetPass
            onRememberPassword={toggleToLogin}
            onSubmit={() => Promise.resolve(toggleToLogin())}
            error={error}
          />
        );
    }
  }, [
    formMode,
    email,
    password,
    error,
    handleLogin,
    handleRegistration,
    handleGoogleSignIn,
    toggleToLogin,
    toggleToRegister,
    toggleToResetPassword,
  ]);

  return (
    <div className="relative flex flex-col min-h-screen bg-black overflow-hidden">
      {/* Background component - extracted to prevent re-renders */}
      <AnimatedBackground />

      {/* Enhanced back button */}
      <BackButton onClick={handleBackNavigation} />

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-4 pt-8 pb-12 relative z-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={formMode}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="w-full max-w-md"
          >
            {/* Form shadow/glow */}
            <div className="absolute inset-0 -z-10 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-blue-500/20 blur-xl rounded-3xl opacity-60 transform scale-110" />

            {currentForm}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="text-center text-gray-600 text-sm py-4 relative z-10">
        © {new Date().getFullYear()} Watch Inspect. All rights reserved.
      </div>

      {/* Already logged in modal */}
      <AnimatePresence>
        {showAlreadyLoggedInModal && user && (
          <AlreadyLoggedInModal
            user={user}
            onClose={closeModal}
            onLogout={handleLogout}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default Auth;
